import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CreationCard } from "@/components/CreationCard";
import { TOOLS, CORE_TOOLS, MORE_TOOLS } from "@/config/tools";
import { SERVICE_BADGES } from "@/config/badges";
import { ShuffleImage } from "@/components/ShuffleImage";
import { GALLERY } from "@/pages/Examples";
import { ArrowRight, Sparkles } from "lucide-react";
import client from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

// Group the shared Examples gallery by tag so each collage card gets its own
// pool of matching visuals to slide through. Fallback to the whole gallery
// when a specific tag has fewer than 2 matches so every card has enough to cycle.
const byTag = (t) => {
  const matches = GALLERY.filter((g) => g.tag === t).map((g) => g.url);
  const fallback = GALLERY.map((g) => g.url);
  return matches.length >= 2 ? matches : [...matches, ...fallback];
};

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showMore, setShowMore] = useState(false);
  const [enabled, setEnabled] = useState(null);
  const [tiles, setTiles] = useState(null);
  const [heroCards, setHeroCards] = useState(null);

  useEffect(() => {
    client.get("/tools").then(({ data }) => setEnabled(new Set(data.map((t) => t.service)))).catch(() => setEnabled(new Set(Object.keys(TOOLS))));
    client.get("/home-tiles").then(({ data }) => setTiles(Array.isArray(data) ? data : [])).catch(() => setTiles([]));
    client.get("/hero-cards").then(({ data }) => setHeroCards(Array.isArray(data) ? data : [])).catch(() => setHeroCards([]));
  }, []);

  const isOn = (s) => !enabled || enabled.has(s);
  const cta = () => navigate(user ? "/create" : "/login");
  const toolsCount = useMemo(() => (enabled ? enabled.size : Object.keys(TOOLS).length), [enabled]);

  const productUrls = byTag("Product Ad");
  const restaurantUrls = byTag("Restaurant Ad");
  const socialUrls = byTag("Social Post");
  // "Fashion" isn't an explicit tag in the existing gallery; use the closest
  // creative-imagery pool ("AI Image") so this card still slides through real
  // gallery assets rather than repeating a single frame.
  const fashionUrls = byTag("AI Image");
  const madeWithUrls = GALLERY.map((g) => g.url);

  return (
    <div>
      {/* HERO */}
      <section className="relative isolate overflow-hidden" style={{ background: "linear-gradient(180deg, #01060f 0%, #020817 100%)" }}>
        {/* Cyan grid pattern overlay, fading out toward the bottom */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-[80%]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(0,207,255,0.09) 1px, transparent 1px), linear-gradient(90deg, rgba(0,207,255,0.09) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage: "linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.55) 45%, transparent 92%)",
            WebkitMaskImage: "linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.55) 45%, transparent 92%)",
          }}
        />
        {/* Soft glow */}
        <div aria-hidden="true" className="pointer-events-none absolute -top-40 left-1/3 h-[520px] w-[520px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(0,229,255,0.20), transparent 70%)" }} />

        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-20 sm:px-8 md:grid-cols-12 md:py-28">
          <div className="md:col-span-6">
            <p className="eyebrow mb-6 inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-3 py-1.5 text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Create. Advertise. Grow.
            </p>
            <h1 className="text-5xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-[64px]">
              Create stunning visuals<br />
              <span className="text-primary">with AI.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg" style={{ color: "#A9BDD0" }}>
              Generate professional images, advertisements and social creatives in seconds — no design skills required.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                onClick={cta}
                data-testid="hero-start-creating-button"
                className="group inline-flex items-center gap-2 rounded-full px-7 py-3 text-base font-semibold text-[#020817] shadow-[0_10px_30px_-8px_rgba(0,207,255,0.65)] transition-transform hover:-translate-y-0.5"
                style={{ background: "linear-gradient(135deg, #00CFFF 0%, #00E5FF 55%, #19F5D2 130%)" }}
              >
                <span className="text-lg">✦</span> Start creating
              </button>
              <button
                onClick={() => document.getElementById("create-area")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-explore-tools-button"
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-base font-semibold text-white/90 transition-colors hover:text-primary"
              >
                Explore tools <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            <ul className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm" style={{ color: "#A9BDD0" }}>
              <li className="inline-flex items-center gap-2"><span className="inline-block h-2 w-2 rounded-full" style={{ background: "#19F5D2", boxShadow: "0 0 10px #19F5D2" }} />Commercial license</li>
              <li className="inline-flex items-center gap-2"><span className="inline-block h-2 w-2 rounded-full" style={{ background: "#19F5D2", boxShadow: "0 0 10px #19F5D2" }} />Instant results</li>
              <li className="inline-flex items-center gap-2"><span className="inline-block h-2 w-2 rounded-full" style={{ background: "#19F5D2", boxShadow: "0 0 10px #19F5D2" }} />{toolsCount} creative tools</li>
            </ul>
          </div>

          {/* COLLAGE */}
          <div className="md:col-span-6">
            <HeroCollage
              fallback={{
                product: productUrls,
                fashion: fashionUrls,
                restaurant: restaurantUrls,
                social: socialUrls,
              }}
              cards={heroCards || []}
            />
          </div>
        </div>
      </section>

      {/* MADE WITH ADGEN STORE */}
      <section className="relative border-t border-border/70" style={{ background: "#01060f" }}>
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <div className="mb-8 flex flex-wrap items-baseline justify-between gap-3">
            <h2 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl">Made with AdGen Store</h2>
            <p className="text-sm" style={{ color: "#71859A" }} data-testid="madewith-categories">
              {(tiles || []).map((t) => t.display_name).join(" · ")}
            </p>
          </div>
          {(tiles && tiles.length > 0) ? (
            <div
              className="grid gap-4 sm:gap-5"
              style={{
                gridTemplateColumns: `repeat(auto-fit, minmax(${tiles.length <= 4 ? "220px" : "180px"}, 1fr))`,
              }}
              data-testid="madewith-grid"
            >
              {tiles.map((t, i) => {
                const adminImgs = t.images || [];
                const urls = adminImgs.length > 0 ? adminImgs : rotateStart(madeWithUrls, i * 2);
                return (
                  <div key={t.key} className="relative overflow-hidden rounded-2xl border border-primary/15 bg-[#0A1930]" data-testid={`madewith-thumb-${t.key}`}>
                    <ShuffleImage urls={urls} alt={t.display_name} intervalMs={4400} className="aspect-[4/5]" />
                    <span className="absolute bottom-3 left-3 rounded-full bg-black/60 px-2.5 py-0.5 text-[11px] font-semibold text-white/90 backdrop-blur">{t.display_name}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No tiles configured yet.</p>
          )}
        </div>
      </section>

      {/* CREATE AREA */}
      <section id="create-area" className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="mb-10 text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">What would you like to create?</h2>
          <p className="mt-3 text-muted-foreground">Choose a creation type to get started.</p>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {CORE_TOOLS.filter(isOn).map((s, i) => (
            <CreationCard key={s} service={s} name={TOOLS[s].title} description={TOOLS[s].subtitle} icon={TOOLS[s].icon} badge={SERVICE_BADGES[s]} index={i} />
          ))}
        </div>

        <div id="tools" className="mt-8 text-center">
          {!showMore ? (
            <Button variant="ghost" data-testid="view-all-tools-button" onClick={() => setShowMore(true)} className="rounded-full">
              View all tools <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {MORE_TOOLS.filter(isOn).map((s, i) => (
                <CreationCard key={s} service={s} name={TOOLS[s].title} description={TOOLS[s].subtitle} icon={TOOLS[s].icon} badge={SERVICE_BADGES[s]} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* MODELS */}
      <section className="border-y border-border/70 bg-secondary/30">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 py-16 sm:px-8 md:grid-cols-2">
          <div className="studio-card p-8">
            <p className="eyebrow text-muted-foreground">Basic</p>
            <h3 className="mt-2 text-2xl font-bold">Fast and affordable</h3>
            <p className="mt-3 text-muted-foreground">Great for quick drafts, social posts and everyday creatives. Speedy generations that keep your credits light.</p>
          </div>
          <div className="studio-card relative overflow-hidden p-8">
            <span className="absolute right-5 top-5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">Recommended</span>
            <p className="eyebrow text-primary">Premium</p>
            <h3 className="mt-2 text-2xl font-bold">Professional quality</h3>
            <p className="mt-3 text-muted-foreground">Ultra-detailed, photoreal output for advertisements and hero visuals where quality matters most.</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-20 text-center sm:px-8">
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Ready to create something amazing?</h2>
        <p className="mx-auto mt-4 max-w-xl text-muted-foreground">Join AdGen Store and turn ideas into professional creatives in seconds.</p>
        <Button size="lg" data-testid="footer-cta-button" onClick={cta} className="mt-8 rounded-full px-8 text-base">Start Creating <ArrowRight className="ml-2 h-5 w-5" /></Button>
      </section>
    </div>
  );
}

/* -------------------- Hero collage (5 floating cards) -------------------- */

const rotateStart = (arr, offset) => {
  if (!arr.length) return arr;
  const o = offset % arr.length;
  return [...arr.slice(o), ...arr.slice(0, o)];
};

function HeroCollage({ fallback, cards }) {
  // Map admin cards by key so we can look up each slot's admin overrides.
  const byKey = Object.fromEntries((cards || []).map((c) => [c.key, c]));
  // Default label for each slot when the admin hasn't set one and the initial
  // seed had one (Restaurant/Social start without a label per current design).
  const defaults = {
    product:    { defaultLabel: "Product Ad" },
    fashion:    { defaultLabel: "Fashion" },
    restaurant: { defaultLabel: null },
    social:     { defaultLabel: null },
  };
  // For each slot: image priority is
  //   1) images from the linked "Made with AdGen Store" tile (`image_urls[]`)
  //   2) admin-uploaded single override (`image_url`)
  //   3) built-in tag-filtered gallery fallback pool
  // Label priority: admin's explicit value (may be null) wins; else built-in default.
  const resolve = (slotKey) => {
    const c = byKey[slotKey] || {};
    let urls;
    if (Array.isArray(c.image_urls) && c.image_urls.length > 0) urls = c.image_urls;
    else if (c.image_url) urls = [c.image_url];
    else urls = fallback[slotKey] || [];
    const label = ("label" in c) ? c.label : defaults[slotKey].defaultLabel;
    return { urls, label };
  };

  const prod = resolve("product");
  const fash = resolve("fashion");
  const rest = resolve("restaurant");
  const soc  = resolve("social");

  return (
    <div className="relative mx-auto aspect-[10/9] w-full max-w-[580px]" data-testid="hero-collage">
      <CollageCard
        style={{ top: "0%", left: "0%", width: "58%", height: "56%", transform: "rotate(-6deg)", zIndex: 2 }}
        label={prod.label} testId="collage-product">
        <ShuffleImage urls={prod.urls} alt="Product ad" intervalMs={4200} />
      </CollageCard>
      <CollageCard
        style={{ top: "4%", right: "0%", width: "56%", height: "54%", transform: "rotate(6deg)", zIndex: 3 }}
        label={fash.label} testId="collage-fashion">
        <ShuffleImage urls={fash.urls} alt="Fashion" intervalMs={4600} />
      </CollageCard>
      <CollageCard
        style={{ bottom: "2%", left: "3%", width: "52%", height: "48%", transform: "rotate(-4deg)", zIndex: 4 }}
        label={rest.label} testId="collage-restaurant">
        <ShuffleImage urls={rest.urls} alt="Restaurant ad" intervalMs={5000} />
      </CollageCard>
      <CollageCard
        style={{ bottom: "0%", right: "2%", width: "62%", height: "56%", transform: "rotate(4deg)", zIndex: 5 }}
        label={soc.label} testId="collage-social">
        <ShuffleImage urls={soc.urls} alt="Social media" intervalMs={4800} />
      </CollageCard>
    </div>
  );
}

function CollageCard({ children, style, label, testId }) {
  return (
    <div
      className="absolute overflow-hidden rounded-2xl border border-primary/20 shadow-[0_25px_50px_-18px_rgba(0,207,255,0.4)]"
      style={{ background: "#0A1930", ...style }}
      data-testid={testId}
    >
      {children}
      {label && (
        <span className="absolute bottom-2.5 left-2.5 rounded-full bg-black/70 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white/95 backdrop-blur">{label}</span>
      )}
    </div>
  );
}
